import { ChevronDown } from "lucide-react";
import { useEffect, useMemo } from "react";
import type { AUTDevice, AUTPropertyDevice } from "@/api/queries/account";
import { ControllerConfigurationPanel, type ControllerConfigValue } from "@/components/ControllerConfigurationPanel";
import Button from "@/components/ui/Button";
import type { VirtualDeviceConfigurationWithModel } from "@/store/data/property-domain";
import { isLICDevice } from "@/utils/mesh-device-utils";

interface WaterPump {
  id: string;
  label: string;
  identifier: string;
  pumpType: "IRRIGATION" | "FERTIGATION" | "SERVICE";
  pumpModel: string;
  controller: VirtualDeviceConfigurationWithModel | null;
}

interface ProjectConfig {
  id: string;
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  irrigationControllerId: string | null;
  waterPumpId: string | null;
  fertigationPumpId: string | null;
  pipeWashTimeMinutes: number | null; // in minutes
  backwashDurationMinutes: number | null; // in minutes
  backwashPeriodMinutes: number | null; // in minutes
}

interface ProjectConfigPanelProps {
  formData: ProjectConfig;
  devices: AUTPropertyDevice[];
  waterPumps: WaterPump[];
  fertigationPumps: WaterPump[];
  saving: boolean;
  onInputChange: (field: keyof ProjectConfig, value: string | null) => void;
  onSave: () => void;
  onCancel: () => void;
  // Backwash controller configuration
  backwashControllerConfig: ControllerConfigValue;
  onBackwashControllerConfigChange: (config: ControllerConfigValue) => void;
  currentBackwashConfig?: VirtualDeviceConfigurationWithModel | null;
}

function ProjectConfigPanel({
  formData,
  devices,
  waterPumps,
  fertigationPumps,
  saving,
  onInputChange,
  onSave,
  onCancel,
  backwashControllerConfig,
  onBackwashControllerConfigChange,
  currentBackwashConfig,
}: ProjectConfigPanelProps) {
  // Validation for required fields
  const isValid = () => {
    const hasName = formData.name.trim() !== "";
    return hasName;
  };

  // If no irrigation controller is selected, force-clear pump selections
  useEffect(() => {
    if (!formData.irrigationControllerId) {
      if (formData.waterPumpId) onInputChange("waterPumpId", "");
      if (formData.fertigationPumpId) onInputChange("fertigationPumpId", "");
    }
    // We intentionally omit onInputChange from deps to avoid re-creating effect unnecessarily
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.irrigationControllerId, formData.waterPumpId, formData.fertigationPumpId]);

  // Get selected pump controllers
  const selectedIrrigationPump = useMemo(() => {
    return waterPumps.find((pump) => pump.id === formData.waterPumpId);
  }, [formData.waterPumpId, waterPumps]);

  const selectedFertigationPump = useMemo(() => {
    return fertigationPumps.find((pump) => pump.id === formData.fertigationPumpId);
  }, [formData.fertigationPumpId, fertigationPumps]);

  // Compute available backwash controllers based on pump selections
  // Only devices that control the selected irrigation OR fertigation pump can be used
  const availableBackwashControllers = useMemo(() => {
    const controllers: AUTDevice[] = [];
    const seenIds = new Set<string>();

    // Add irrigation pump controller if exists
    const irrigationControllerDevice = selectedIrrigationPump?.controller?.device;
    if (irrigationControllerDevice && !seenIds.has(irrigationControllerDevice.id)) {
      seenIds.add(irrigationControllerDevice.id);
      // The device from VirtualDeviceConfigurationWithModel has the required fields for ControllerConfigurationPanel
      controllers.push(irrigationControllerDevice as unknown as AUTDevice);
    }

    // Add fertigation pump controller if exists and different from irrigation
    const fertigationControllerDevice = selectedFertigationPump?.controller?.device;
    if (fertigationControllerDevice && !seenIds.has(fertigationControllerDevice.id)) {
      seenIds.add(fertigationControllerDevice.id);
      controllers.push(fertigationControllerDevice as unknown as AUTDevice);
    }

    return controllers;
  }, [selectedIrrigationPump, selectedFertigationPump]);

  // Check if backwash configuration is possible
  const canConfigureBackwash = availableBackwashControllers.length > 0;

  // When pump selections change, clear backwash if the selected controller is no longer available
  useEffect(() => {
    if (backwashControllerConfig.controllerId) {
      const isStillAvailable = availableBackwashControllers.some((c) => c.id === backwashControllerConfig.controllerId);
      if (!isStillAvailable) {
        onBackwashControllerConfigChange({
          controllerId: null,
          turnOnOutputIndex: null,
          turnOffOutputIndex: null,
          monitoringInputIndex: null,
          outputMode: "continuous",
          inputMode: "digital",
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableBackwashControllers]);

  return (
    <div className="p-6 bg-white">
      {/* Project Name */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-neutral-700 mb-2">
          Nome <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => onInputChange("name", e.target.value)}
          className="w-full px-4 py-3 border border-neutral-200 rounded-xl text-base focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all duration-150 ease-in-out min-h-12"
          placeholder="Nome do projeto"
          required
        />
      </div>

      {/* Project Description */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-neutral-700 mb-2">Descrição</label>
        <textarea
          value={formData.description}
          onChange={(e) => onInputChange("description", e.target.value)}
          className="w-full px-4 py-3 border border-neutral-200 rounded-xl text-base focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all duration-150 ease-in-out resize-vertical"
          placeholder="Descrição do projeto (opcional)"
          rows={3}
        />
      </div>

      {/* Irrigation Controller */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-neutral-700 mb-2">Controlador de Irrigação</label>
        <div className="relative">
          <select
            value={formData.irrigationControllerId || ""}
            onChange={(e) => {
              const val = e.target.value;
              onInputChange("irrigationControllerId", val);
              if (!val) {
                // Clear dependent fields when LIC is unset
                onInputChange("waterPumpId", "");
                onInputChange("fertigationPumpId", "");
              }
            }}
            className="w-full px-4 py-3 border border-neutral-200 rounded-xl text-base focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all duration-150 ease-in-out appearance-none bg-white min-h-12"
          >
            <option value="">Nenhum (opcional)</option>
            {devices
              .filter((d) => isLICDevice(d.device.model))
              .map((prop_device) => (
                <option key={prop_device.device.id} value={prop_device.device.id}>
                  {prop_device.device.metadata?.label
                    ? `${prop_device.device.metadata.label} - ${prop_device.device.identifier}`
                    : prop_device.device.identifier}
                </option>
              ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-neutral-400 pointer-events-none" />
        </div>
      </div>

      {/* Water Pump */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-neutral-700 mb-2">Bomba de Irrigação</label>
        <div className="relative">
          <select
            value={formData.waterPumpId || ""}
            onChange={(e) => onInputChange("waterPumpId", e.target.value)}
            className="w-full px-4 py-3 border border-neutral-200 rounded-xl text-base focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all duration-150 ease-in-out appearance-none bg-white min-h-12 disabled:bg-neutral-50 disabled:text-neutral-400"
            disabled={!formData.irrigationControllerId}
          >
            <option value="">Nenhuma</option>
            {waterPumps.map((pump) => (
              <option key={pump.id} value={pump.id}>
                {pump.label} ({pump.identifier})
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-neutral-400 pointer-events-none" />
        </div>
      </div>

      {/* Fertigation Pump */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-neutral-700 mb-2">Bomba de Fertirrigação</label>
        <div className="relative">
          <select
            value={formData.fertigationPumpId || ""}
            onChange={(e) => onInputChange("fertigationPumpId", e.target.value)}
            className="w-full px-4 py-3 border border-neutral-200 rounded-xl text-base focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all duration-150 ease-in-out appearance-none bg-white min-h-12 disabled:bg-neutral-50 disabled:text-neutral-400"
            disabled={!formData.irrigationControllerId}
          >
            <option value="">Nenhuma</option>
            {fertigationPumps.map((pump) => (
              <option key={pump.id} value={pump.id}>
                {pump.label} ({pump.identifier})
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-neutral-400 pointer-events-none" />
        </div>
      </div>

      {/* Backwash Controller Configuration */}
      <div className="mb-6">
        <h3 className="text-sm font-medium text-neutral-700 mb-4">Acionamento da Retrolavagem</h3>
        {!canConfigureBackwash ? (
          <div className="p-4 border border-neutral-200 rounded-xl bg-neutral-50">
            <p className="text-sm text-neutral-500">
              Selecione uma bomba de irrigação ou fertirrigação com controlador para configurar a retrolavagem.
            </p>
          </div>
        ) : (
          <>
            <ControllerConfigurationPanel
              equipmentType="project_backwash"
              currentConfig={currentBackwashConfig}
              value={backwashControllerConfig}
              onChange={onBackwashControllerConfigChange}
              availableControllers={availableBackwashControllers}
              disabled={!canConfigureBackwash}
              showTurnOffOutput={false}
              showMonitoringInput={false}
              showModeSelection={false}
            />
            {availableBackwashControllers.length === 1 && (
              <p className="text-xs text-neutral-500 mt-2">
                Apenas o controlador da bomba selecionada está disponível para retrolavagem.
              </p>
            )}
            {availableBackwashControllers.length > 1 && (
              <p className="text-xs text-neutral-500 mt-2">
                Você pode escolher entre os controladores das bombas de irrigação e fertirrigação.
              </p>
            )}
          </>
        )}
      </div>

      {/* Irrigation System Configuration */}
      <div className="mb-6">
        <h3 className="text-lg font-medium text-neutral-900 mb-4">Configuração do Sistema de Irrigação</h3>

        <div className="grid grid-cols-1 gap-4">
          {/* Pipe Wash Time */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Tempo de Lavagem dos Tubos (minutos)
            </label>
            <input
              type="number"
              value={formData.pipeWashTimeMinutes ?? ""}
              onChange={(e) => onInputChange("pipeWashTimeMinutes", e.target.value)}
              className="w-full px-4 py-3 border border-neutral-200 rounded-xl text-base focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all duration-150 ease-in-out min-h-12"
              placeholder="Ex: 5"
              min="0"
              max="60"
            />
            <p className="text-xs text-neutral-500 mt-1">
              Tempo para lavar tubos após fertirrigação (necessário se bomba de fertirrigação estiver configurada)
            </p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button
          onClick={onSave}
          loading={saving}
          disabled={!isValid()}
          variant="primary"
          size="md"
          className="flex-1 rounded-xl"
        >
          Salvar
        </Button>
        <Button onClick={onCancel} variant="secondary" size="md" className="flex-1 rounded-xl">
          Cancelar
        </Button>
      </div>

      {/* Required fields note */}
      {!isValid() && (
        <p className="text-sm text-neutral-500 mt-4 text-center">
          <span className="text-red-500">*</span> Campos obrigatórios
        </p>
      )}
    </div>
  );
}

export default ProjectConfigPanel;
